import React, { createContext, type ReactNode, useContext, useMemo } from "react"
import type { ProviderAttachmentKind, ProviderThreadList, ProviderThreadMessages } from "../providerMessagingTypes"

/**
 * The PROVIDER (Healthie) messaging transport, reached through the host.
 *
 * ERROR SHAPE: every method's rejections must carry a numeric `statusCode` (`ProviderApiError`),
 * which is what the retry switches in `hooks/useProviderThreads.ts` and `hooks/useThreadMessages.ts`
 * read. Mapping the transport's own errors into that shape is the HOST adapter's job. That is what
 * keeps the package transport-agnostic: nothing here imports the BFF client or knows that tRPC
 * exists.
 */
export type MessagesProviderApi = {
  /**
   * The member's provider conversations — ALL of them, open and closed. NO PARAMETERS: the transport
   * answers the whole list, and there is no "load more" control anywhere in this feature for a page
   * boundary to serve.
   *
   * Answers with `{ threads, hasProviderIdentity }` rather than the array itself. The flag is a
   * property of the MEMBER, not of the list, so an empty `threads` alone cannot express it: see
   * `ProviderThreadList`. A host whose transport genuinely cannot tell should report `true` — the
   * conservative answer, since it claims nothing about what the member does or does not have.
   */
  getThreads: () => Promise<ProviderThreadList>
  /** One thread's whole history, oldest-first. Unpaged, for the same reason `getThreads` is. */
  getThreadMessages: (params: { threadId: string }) => Promise<ProviderThreadMessages>
  /** `clientMessageId` is the caller's idempotency key for the send. */
  sendMessage: (params: {
    threadId: string
    body: string
    clientMessageId: string
  }) => Promise<{ messageId: string; createdAt: number }>
  markRead: (params: { threadId: string }) => Promise<void>
  /**
   * Where a member's click on one of a message's attachments goes.
   *
   * ⚠ A URL, NOT A CALL, and the only member of this contract that is not a promise. That is the
   * point of it: Healthie signs every attachment url with a ten-second expiry, so the file cannot be
   * fetched ahead of the click and handed over — the href has to be a STABLE url that mints one when
   * followed, and following it is a navigation the browser performs, not a request this package
   * makes. (Fetching it here would also mean holding a bearer credential for a patient's file in
   * front-end memory.)
   *
   * It is on the HOST contract rather than built in this package for the same reason the transport
   * is: the BFF's path is the host's to know. The consumer app answers `/bff/messages/attachment/…`.
   */
  attachmentUrl: (params: { messageId: string; kind: ProviderAttachmentKind }) => string
}

// The HOST contract handed into the provider messages panel.
//
// It exists because everything this feature needs from the app is unreachable from a workspace
// package: the BFF client is host-only, and so is Sentry. The `#/*` alias is a root-tsconfig path, so
// no `packages/*` file can import any of it.
//
// The split is deliberate: the host supplies TRANSPORT and error reporting, the package owns the
// react-query layer. Cache keys, `staleTime` and the retry switches live in the package, once each.
//
// THREE KEYS, where this used to carry the whole Carrot messaging surface as well — the second half
// of a page that no longer lives here. Provider messaging is now the package's only subject: the
// Carrot side of /messages is the app's own (src/components/messaging/**), framed by the app's own
// shell (src/pages/messages-shell.tsx), and the tab bar there is what puts the two beside each other.
// Nothing in this package renders host chrome any more, so `components` is gone with it.
//
// Mirrors the pattern in packages/consumer/src/subscription/context/SubscriptionHostContext.tsx.
export type MessagesHostContract = {
  // REQUIRED, unlike its predecessor. It used to be optional because the package served a host that
  // might have no provider transport at all; the panel it serves now IS the provider transport's UI,
  // so a host without one has nothing to mount. `useProviderUnreadCount` still takes its `getThreads`
  // as an ordinary parameter that may be absent — that is the nav dot deciding not to ask, which is a
  // different thing from a host that cannot answer.
  providerApi: MessagesProviderApi
  // The host's Sentry wrappers (src/utils/ErrorReporting.ts).
  reportError: (error: unknown, context?: Record<string, string>) => void
  reportErrorMessage: (message: string) => void
}

const MessagesHostContext = createContext<MessagesHostContract | null>(null)

export function useMessagesHost(): MessagesHostContract {
  const ctx = useContext(MessagesHostContext)
  if (ctx === null) {
    throw new Error("useMessagesHost must be used within <MessagesHostProvider>")
  }
  return ctx
}

export function MessagesHostProvider({
  providerApi,
  reportError,
  reportErrorMessage,
  children
}: MessagesHostContract & { children: ReactNode }): JSX.Element {
  const value = useMemo<MessagesHostContract>(
    () => ({ providerApi, reportError, reportErrorMessage }),
    [providerApi, reportError, reportErrorMessage]
  )
  return <MessagesHostContext.Provider value={value}>{children}</MessagesHostContext.Provider>
}
